import { useOnMount } from "./useOnMount";
import { useIoContext, useStore, useStoreStatic } from "../contexts";
import { getAuthTokenFromCookie } from "../utils/cookie.utils";
import { selectors } from "../store/state-selectors";
import { useDidUpdate } from 'seagull';
import * as Sentry from '@sentry/react';
const userDataStorageKey = 'userData';
function safeJSONParse(json) {
  if (typeof json !== 'string') return null;
  try {
    return JSON.parse(json);
  } catch (err) {
    return null;
  }
}
function isMismatchingVersion(oldValue, newValue) {
  const oldPayload = safeJSONParse(oldValue);
  const newPayload = safeJSONParse(newValue);
  return !!oldPayload && !!newPayload && oldPayload.version !== newPayload.version;
}

/** Syncs the authToken between multiple active browser-tabs. Makes sure all tabs are set to the same user and business. */
export function useSyncUserDataBetweenTabs() {
  const userInfo = useStore(state => state.userInfo);
  const {
    getState
  } = useStoreStatic();
  const {
    locationReload
  } = useIoContext();
  useOnMount(() => {
    async function onStorageEvent(event) {
      if (event.key === userDataStorageKey && event.oldValue !== event.newValue && !isMismatchingVersion(event.oldValue, event.newValue) && !selectors.isResetPasswordFlow(getState())) {
        const authToken = getAuthTokenFromCookie(document.cookie);
        Sentry.addBreadcrumb({
          category: 'syncUserData',
          data: {
            oldValue: event.oldValue,
            newValue: event.newValue,
            authToken
          }
        });
        locationReload();
      }
    }
    window.addEventListener('storage', onStorageEvent);
    return () => {
      window.removeEventListener('storage', onStorageEvent);
    };
  });
  useDidUpdate(() => {
    var _getState$claims;
    const data = userInfo ? {
      userId: userInfo.id || null,
      // We use claims here to get the data from the cookie instead of userInfo.worksAt.
      // In "create additional account" flow we artificially reset the userInfo.worksAt,
      // and we don't want that to affect other tabs.
      businessId: ((_getState$claims = getState().claims) === null || _getState$claims === void 0 ? void 0 : _getState$claims.businessId) || null,
      // Increment this counter when changing UserData schema, to avoid a feedback loop between multiple tabs.
      version: 1
    } : null;
    localStorage.setItem(userDataStorageKey, JSON.stringify(data));
  }, [userInfo]);
}